import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { PrimarySelectionMethod } from "types/ProfessionalRuleset/ProfessionalRuleset";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export interface IInstitutionalRulesetFormData {
  // Single-scope (edit mode).
  providerId: string | null;
  payerId: number | null;
  // Multi-scope (create mode) — N providers × M payers fan-out.
  providerIds: string[];
  payerIds: number[];
  primarySelectionMethod: PrimarySelectionMethod;
}

export const INITIAL_INSTITUTIONAL_FORM_DATA: IInstitutionalRulesetFormData = {
  providerId: null,
  payerId: null,
  providerIds: [],
  payerIds: [],
  primarySelectionMethod: "CONTRACTED_DOLLARS",
};

/**
 * Only RVU and contracted dollars are offered. The other two backend values
 * (ENDO_FAMILY_RVU, BILATERAL_RVU_ADJUSTED) are endoscopy/bilateral concepts that only apply to
 * professional billing — facility lines never form endoscopy families, so offering them here
 * would present options that silently do nothing.
 */
export const INSTITUTIONAL_PRIMARY_SELECTION_OPTIONS: Array<{
  label: string;
  value: PrimarySelectionMethod;
}> = [
  { label: "Contracted dollars", value: "CONTRACTED_DOLLARS" },
  { label: "RVU (per line)", value: "RVU" },
];

export const INSTITUTIONAL_PRIMARY_SELECTION_HELP: Partial<
  Record<PrimarySelectionMethod, string>
> = {
  CONTRACTED_DOLLARS:
    "Default. Every line ranks by contracted dollars — the highest fee-schedule amount is the 100% primary. This is what facility remits show payers actually doing.",
  RVU: "Every line ranks by total RVU instead. Use only for a contract with evidence the payer ranks this way.",
};

interface IInstitutionalRulesetFormProps {
  title: string;
  submitLabel: string;
  formData: IInstitutionalRulesetFormData;
  onChange: (data: IInstitutionalRulesetFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /**
   * In edit mode, scope (provider+payer) is the composite key and is locked.
   */
  scopeLocked?: boolean;
  /**
   * In create mode, allow selecting multiple providers and payers; the backend fans out
   * into one ruleset per (provider, payer) pair.
   */
  multiScope?: boolean;
}

export function InstitutionalRulesetForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
  multiScope,
}: IInstitutionalRulesetFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  // Institutional rulesets only ever apply to a facility — the engine resolves the method by
  // provider specialty — so offering non-facility providers here would create rows that never
  // price anything.
  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data
      .filter((p: any) => p.specialty === "FACILITY")
      .map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  const handleChange = (
    field: keyof IInstitutionalRulesetFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid = multiScope
    ? formData.providerIds.length > 0 &&
      formData.payerIds.length > 0 &&
      !isSubmitting
    : formData.providerId !== null &&
      formData.payerId !== null &&
      !isSubmitting;

  const pairCount = formData.providerIds.length * formData.payerIds.length;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <FieldGrid>
          {multiScope ? (
            <>
              <Field>
                <label htmlFor="providerIds">Facilities</label>
                <MultiSelect
                  inputId="providerIds"
                  value={formData.providerIds}
                  options={providerOptions}
                  onChange={(e) => handleChange("providerIds", e.value)}
                  placeholder="Select one or more facilities"
                  display="chip"
                  filter
                />
              </Field>
              <Field>
                <label htmlFor="payerIds">Payers</label>
                <MultiSelect
                  inputId="payerIds"
                  value={formData.payerIds}
                  options={payerOptions}
                  onChange={(e) => handleChange("payerIds", e.value)}
                  placeholder="Select one or more payers"
                  display="chip"
                  filter
                />
              </Field>
            </>
          ) : (
            <>
              <Field>
                <label htmlFor="providerId">Facility</label>
                <Dropdown
                  inputId="providerId"
                  value={formData.providerId}
                  options={providerOptions}
                  onChange={(e) => handleChange("providerId", e.value)}
                  placeholder="Select a facility"
                  filter
                  disabled={scopeLocked}
                />
              </Field>
              <Field>
                <label htmlFor="payerId">Payer</label>
                <Dropdown
                  inputId="payerId"
                  value={formData.payerId}
                  options={payerOptions}
                  onChange={(e) => handleChange("payerId", e.value)}
                  placeholder="Select a payer"
                  filter
                  disabled={scopeLocked}
                />
              </Field>
            </>
          )}
        </FieldGrid>
        {multiScope && pairCount > 0 && (
          <p className="text-sm text-color-secondary mt-0 mb-3">
            Creates {pairCount} ruleset
            {pairCount === 1 ? "" : "s"} ({formData.providerIds.length} facility
            {formData.providerIds.length === 1 ? "" : "s"} ×{" "}
            {formData.payerIds.length} payer
            {formData.payerIds.length === 1 ? "" : "s"}), all sharing the
            settings below.
          </p>
        )}

        <Field className="mb-4">
          <label htmlFor="primarySelectionMethod">
            <strong>Primary Selection Method</strong>
          </label>
          <Dropdown
            inputId="primarySelectionMethod"
            value={formData.primarySelectionMethod}
            options={INSTITUTIONAL_PRIMARY_SELECTION_OPTIONS}
            onChange={(e) =>
              handleChange(
                "primarySelectionMethod",
                (e.value ?? "CONTRACTED_DOLLARS") as PrimarySelectionMethod
              )
            }
          />
          <FieldHelp>
            {INSTITUTIONAL_PRIMARY_SELECTION_HELP[
              formData.primarySelectionMethod
            ] ??
              "How the MPPR pass picks the procedure paid at 100% on this facility's claims."}
          </FieldHelp>
        </Field>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}

export default InstitutionalRulesetForm;
